import React, { useEffect, useMemo, useState } from "react";
import {
	Button,
	Card,
	Col,
	Container,
	Form,
	Offcanvas,
	Row,
	Stack,
	Table,
} from "react-bootstrap";
import { useNavigate } from "react-router-dom";
import {
	createLeague,
	getLeagues,
	updateLeague,
	uploadImage,
} from "../../services/leagueService";
import { getSeries, getSports } from "../../services/series";
import { getSeasons } from "../../services/seasonService";
import AdminLayout from "./AdminLayout";

const initialFormData = {
	name: "",
	description: "",
	sport: "",
	series: "",
	season: "",
	privacy: "public",
	prize: "",
	losing_prize: "",
	overallPrizeDescription: "",
	roundPrizeDescription: "",
	overallPrizeUrl: "",
	roundPrizeUrl: "",
	featured: false,
};

const AdminLeaguesPage = () => {
	const navigate = useNavigate();

	const [leagues, setLeagues] = useState([]);
	const [sports, setSports] = useState([]);
	const [series, setSeries] = useState([]);
	const [seasons, setSeasons] = useState([]);
	const [searchQuery, setSearchQuery] = useState("");
	const [filterSport, setFilterSport] = useState("");
	const [filterSeries, setFilterSeries] = useState("");
	const [filterSeason, setFilterSeason] = useState("");
	const [showDrawer, setShowDrawer] = useState(false);
	const [selectedLeague, setSelectedLeague] = useState(null);
	const [formData, setFormData] = useState(initialFormData);
	const [image, setImage] = useState(null);
	const [roundImage, setRoundImage] = useState(null);
	const [imageMobile, setImageMobile] = useState(null);
	const [roundImageMobile, setRoundImageMobile] = useState(null);

	useEffect(() => {
		fetchLeagues();
		fetchSports();
		fetchSeries();
		fetchSeasons();
	}, []);

	const fetchLeagues = async () => {
		try {
			const response = await getLeagues();
			setLeagues(response.data);
		} catch (error) {
			console.error("Error fetching leagues:", error);
		}
	};

	const fetchSports = async () => {
		try {
			const res = await getSports();
			setSports(res.data);
		} catch (error) {
			console.error("Error fetching sports:", error);
		}
	};

	const fetchSeries = async () => {
		try {
			const res = await getSeries();
			setSeries(res.data);
		} catch (error) {
			console.error("Error fetching series:", error);
		}
	};

	const fetchSeasons = async () => {
		try {
			const res = await getSeasons();
			setSeasons(res.data);
		} catch (error) {
			console.error("Error fetching seasons:", error);
		}
	};

	const resetForm = () => {
		setFormData(initialFormData);
		setSelectedLeague(null);
		setImage(null);
		setRoundImage(null);
		setImageMobile(null);
		setRoundImageMobile(null);
	};

	const openCreateDrawer = () => {
		resetForm();
		setShowDrawer(true);
	};

	const openEditDrawer = (league) => {
		setSelectedLeague(league);
		setFormData({
			name: league?.name ?? "",
			description: league?.description ?? "",
			sport: league?.sport?._id ?? league?.sport ?? "",
			series: league?.series?._id ?? league?.series ?? "",
			season: league?.season?._id ?? league?.season ?? "",
			privacy: league?.privacy ?? (league?.private ? "private" : "public"),
			prize: league?.prize ?? "",
			losing_prize: league?.losing_prize ?? "",
			overallPrizeDescription: league?.overallPrizeDescription ?? "",
			roundPrizeDescription: league?.roundPrizeDescription ?? "",
			overallPrizeUrl: league?.overallPrizeUrl ?? "",
			roundPrizeUrl: league?.roundPrizeUrl ?? "",
			featured: Boolean(league?.featured),
		});
		setShowDrawer(true);
	};

	const handleFormChange = (e) => {
		const { name, value, type, checked } = e.target;
		setFormData((prev) => ({
			...prev,
			[name]: type === "checkbox" ? checked : value,
		}));
	};

	const handleSaveLeague = async () => {
		if (!formData.name || !formData.sport || !formData.series || !formData.season) {
			alert("Please complete name, sport, series, and season.");
			return;
		}

		try {
			if (selectedLeague?._id) {
				const updatedLeague = {
					...selectedLeague,
					...formData,
					sport: formData.sport,
					series: formData.series,
					season: formData.season,
					privacy: formData.privacy,
					private: formData.privacy === "private",
					featured: formData.featured,
				};

				const res = await updateLeague(selectedLeague._id, updatedLeague);
				if (res) {
					fetchLeagues();
					setShowDrawer(false);
				}
			} else {
				const payload = {
					...formData,
					privacy: formData.privacy,
					private: formData.privacy === "private",
				};
				const res = await createLeague(payload);
				if (res) {
					fetchLeagues();
					setShowDrawer(false);
				}
			}
		} catch (error) {
			console.error("Error saving league:", error);
			alert("Error saving league");
		}
	};

	const handleImageChange = (e) => {
		setImage(e.target.files[0]);
	};

	const handleRoundImageChange = (e) => {
		setRoundImage(e.target.files[0]);
	};

	const handleImageMobileChange = (e) => {
		setImageMobile(e.target.files[0]);
	};

	const handleRoundImageMobileChange = (e) => {
		setRoundImageMobile(e.target.files[0]);
	};

	const handleUploadImages = async (e) => {
		e.preventDefault();
		if (!selectedLeague?._id) {
			return;
		}

		const formDataPayload = new FormData();
		if (image) formDataPayload.append("image", image);
		if (roundImage) formDataPayload.append("roundImage", roundImage);
		if (imageMobile) formDataPayload.append("imageMobile", imageMobile);
		if (roundImageMobile) {
			formDataPayload.append("roundImageMobile", roundImageMobile);
		}

		try {
			await uploadImage(selectedLeague._id, formDataPayload);
			alert("Images uploaded successfully");
		} catch (error) {
			console.error("Error uploading images:", error);
			alert("Failed to upload images");
		}
	};

	const handleClearImage = async () => {
		if (!selectedLeague?._id) return;
		try {
			const updatedLeague = { ...selectedLeague, image: "" };
			const res = await updateLeague(selectedLeague._id, updatedLeague);
			if (res) {
				fetchLeagues();
			}
		} catch (error) {
			alert("Error clearing image: " + error);
		}
	};

	const handleClearRoundImage = async () => {
		if (!selectedLeague?._id) return;
		try {
			const updatedLeague = { ...selectedLeague, roundImage: "" };
			const res = await updateLeague(selectedLeague._id, updatedLeague);
			if (res) {
				fetchLeagues();
			}
		} catch (error) {
			alert("Error clearing round image: " + error);
		}
	};

	const filteredLeagues = useMemo(() => {
		const query = searchQuery.trim().toLowerCase();
		return leagues
			.filter((league) => {
				const nameMatches = !query
					? true
					: (league?.name ?? "").toLowerCase().includes(query);
				if (!nameMatches) return false;

				const leagueSportId = league?.sport?._id ?? league?.sport ?? "";
				const leagueSeriesId = league?.series?._id ?? league?.series ?? "";
				const leagueSeasonId = league?.season?._id ?? league?.season ?? "";

				if (filterSport && leagueSportId !== filterSport) return false;
				if (filterSeries && leagueSeriesId !== filterSeries) return false;
				if (filterSeason && leagueSeasonId !== filterSeason) return false;

				return true;
			})
			.sort((a, b) => (a?.name ?? "").localeCompare(b?.name ?? ""));
	}, [leagues, searchQuery, filterSport, filterSeries, filterSeason]);

	const getSportName = (league) => {
		if (league?.sport?.name) return league.sport.name;
		const sport = sports.find((item) => item._id === league?.sport);
		return sport?.name ?? "-";
	};

	const getSeriesName = (league) => {
		if (league?.series?.name) return league.series.name;
		const seriesItem = series.find((item) => item._id === league?.series);
		return seriesItem?.name ?? "-";
	};

	const getSeasonName = (league) => {
		const seasonId = league?.season?._id ?? league?.season;
		const seasonItem = seasons.find((item) => item._id === seasonId);
		return seasonItem?.name ?? "-";
	};

	const getPrivacyLabel = (league) => {
		return league?.privacy ?? (league?.private ? "private" : "public");
	};

	return (
		<AdminLayout>
			<Container className="py-4">
				<Row className="align-items-end mb-3 g-3">
					<Col md={4}>
						<Form.Group>
							<Form.Label>Search Leagues</Form.Label>
							<Form.Control
								type="text"
								placeholder="Search by league name"
								value={searchQuery}
								onChange={(e) => setSearchQuery(e.target.value)}
								className="admin-search-input"
							/>
						</Form.Group>
					</Col>
					<Col md={2}>
						<Form.Group>
							<Form.Label>Sport</Form.Label>
							<Form.Select
								value={filterSport}
								onChange={(e) => setFilterSport(e.target.value)}
							>
								<option value="">All</option>
								{sports.map((sport) => (
									<option key={sport._id} value={sport._id}>
										{sport.name}
									</option>
								))}
							</Form.Select>
						</Form.Group>
					</Col>
					<Col md={2}>
						<Form.Group>
							<Form.Label>Series</Form.Label>
							<Form.Select
								value={filterSeries}
								onChange={(e) => setFilterSeries(e.target.value)}
							>
								<option value="">All</option>
								{series.map((seriesOption) => (
									<option key={seriesOption._id} value={seriesOption._id}>
										{seriesOption.name}
									</option>
								))}
							</Form.Select>
						</Form.Group>
					</Col>
					<Col md={2}>
						<Form.Group>
							<Form.Label>Season</Form.Label>
							<Form.Select
								value={filterSeason}
								onChange={(e) => setFilterSeason(e.target.value)}
							>
								<option value="">All</option>
								{seasons.map((season) => (
									<option key={season._id} value={season._id}>
										{season.name}
									</option>
								))}
							</Form.Select>
						</Form.Group>
					</Col>
					<Col md="auto" className="ms-md-auto">
						<Button variant="primary" onClick={openCreateDrawer}>
							Create League
						</Button>
					</Col>
				</Row>
				<Card className="shadow border-0">
					<Card.Body>
						<Table striped bordered hover responsive>
							<thead>
								<tr>
									<th>Name</th>
									<th>Sport</th>
									<th>Series</th>
									<th>Season</th>
									<th>Teams</th>
									<th>Privacy</th>
									<th>Featured</th>
									<th>Actions</th>
								</tr>
							</thead>
							<tbody>
								{filteredLeagues.map((league) => (
									<tr key={league._id}>
										<td>{league?.name}</td>
										<td>{getSportName(league)}</td>
										<td>{getSeriesName(league)}</td>
										<td>{getSeasonName(league)}</td>
										<td>{league?.teams?.length ?? 0}</td>
										<td className="text-capitalize">
											{getPrivacyLabel(league)}
										</td>
										<td>{league?.featured ? "Yes" : "No"}</td>
										<td>
											<Stack direction="horizontal" gap={2}>
												<Button
													variant="outline-primary"
													size="sm"
													onClick={() => openEditDrawer(league)}
												>
													Edit
												</Button>
												<Button
													variant="outline-secondary"
													size="sm"
													onClick={() => navigate(`/league/${league._id}`)}
												>
													View
												</Button>
											</Stack>
										</td>
									</tr>
								))}
							</tbody>
						</Table>
					</Card.Body>
				</Card>
				<Offcanvas
					show={showDrawer}
					onHide={() => setShowDrawer(false)}
					placement="end"
					className="admin-offcanvas"
				>
					<Offcanvas.Header closeButton>
						<Offcanvas.Title>
							{selectedLeague?._id ? "Edit League" : "Create League"}
						</Offcanvas.Title>
					</Offcanvas.Header>
					<Offcanvas.Body>
						<Form>
							<Form.Group controlId="leagueName" className="mb-3">
								<Form.Label>Name</Form.Label>
								<Form.Control
									type="text"
									name="name"
									value={formData.name}
									onChange={handleFormChange}
									required
								/>
							</Form.Group>
							<Form.Group controlId="leagueDescription" className="mb-3">
								<Form.Label>Description</Form.Label>
								<Form.Control
									as="textarea"
									name="description"
									value={formData.description}
									onChange={handleFormChange}
								/>
							</Form.Group>
							<Form.Group controlId="leagueSport" className="mb-3">
								<Form.Label>Sport</Form.Label>
								<Form.Select
									name="sport"
									value={formData.sport}
									onChange={handleFormChange}
									required
								>
									<option value="">Select a sport</option>
									{sports.map((sport) => (
										<option key={sport._id} value={sport._id}>
											{sport.name}
										</option>
									))}
								</Form.Select>
							</Form.Group>
							<Form.Group controlId="leagueSeries" className="mb-3">
								<Form.Label>Series</Form.Label>
								<Form.Select
									name="series"
									value={formData.series}
									onChange={handleFormChange}
									required
								>
									<option value="">Select a series</option>
									{series.map((seriesOption) => (
										<option key={seriesOption._id} value={seriesOption._id}>
											{seriesOption.name}
										</option>
									))}
								</Form.Select>
							</Form.Group>
							<Form.Group controlId="leagueSeason" className="mb-3">
								<Form.Label>Season</Form.Label>
								<Form.Select
									name="season"
									value={formData.season}
									onChange={handleFormChange}
									required
								>
									<option value="">Select a season</option>
									{seasons.map((season) => (
										<option key={season._id} value={season._id}>
											{season.name}
										</option>
									))}
								</Form.Select>
							</Form.Group>
							<Form.Group controlId="leaguePrivacy" className="mb-3">
								<Form.Label>Privacy</Form.Label>
								<Form.Select
									name="privacy"
									value={formData.privacy}
									onChange={handleFormChange}
								>
									<option value="public">Public</option>
									<option value="private">Private</option>
								</Form.Select>
							</Form.Group>
							<Form.Group controlId="leaguePrize" className="mb-3">
								<Form.Label>Prize</Form.Label>
								<Form.Control
									name="prize"
									value={formData.prize}
									onChange={handleFormChange}
								/>
							</Form.Group>
							<Form.Group controlId="leagueLosingPrize" className="mb-3">
								<Form.Label>Losing Prize</Form.Label>
								<Form.Control
									name="losing_prize"
									value={formData.losing_prize}
									onChange={handleFormChange}
								/>
							</Form.Group>
							<Form.Group
								controlId="overallPrizeDescription"
								className="mb-3"
							>
								<Form.Label>Overall Prize Description</Form.Label>
								<Form.Control
									name="overallPrizeDescription"
									value={formData.overallPrizeDescription}
									onChange={handleFormChange}
								/>
							</Form.Group>
							<Form.Group
								controlId="roundPrizeDescription"
								className="mb-3"
							>
								<Form.Label>Round Prize Description</Form.Label>
								<Form.Control
									name="roundPrizeDescription"
									value={formData.roundPrizeDescription}
									onChange={handleFormChange}
								/>
							</Form.Group>
							<Form.Group controlId="overallPrizeUrl" className="mb-3">
								<Form.Label>Overall Prize URL</Form.Label>
								<Form.Control
									name="overallPrizeUrl"
									value={formData.overallPrizeUrl}
									onChange={handleFormChange}
								/>
							</Form.Group>
							<Form.Group controlId="roundPrizeUrl" className="mb-3">
								<Form.Label>Round Prize URL</Form.Label>
								<Form.Control
									name="roundPrizeUrl"
									value={formData.roundPrizeUrl}
									onChange={handleFormChange}
								/>
							</Form.Group>
							<Form.Group controlId="leagueFeatured" className="mb-3">
								<Form.Check
									type="switch"
									name="featured"
									label="Featured League"
									checked={formData.featured}
									onChange={handleFormChange}
								/>
							</Form.Group>
							{selectedLeague?._id && (
								<Form onSubmit={handleUploadImages} className="mb-4">
									<Form.Group controlId="leagueImage" className="mb-3">
										<Form.Label>Main Image</Form.Label>
										<Form.Control
											type="file"
											onChange={handleImageChange}
										/>
									</Form.Group>
									<Stack
										direction="horizontal"
										gap={2}
										className="justify-content-end mb-3"
									>
										<Button
											variant="outline-secondary"
											size="sm"
											onClick={handleClearImage}
										>
											Clear Image
										</Button>
									</Stack>
									<Form.Group controlId="leagueRoundImage" className="mb-3">
										<Form.Label>Round Image</Form.Label>
										<Form.Control
											type="file"
											onChange={handleRoundImageChange}
										/>
									</Form.Group>
									<Stack
										direction="horizontal"
										gap={2}
										className="justify-content-end mb-3"
									>
										<Button
											variant="outline-secondary"
											size="sm"
											onClick={handleClearRoundImage}
										>
											Clear Round Image
										</Button>
									</Stack>
									<Form.Group controlId="leagueImageMobile" className="mb-3">
										<Form.Label>Main Image Mobile</Form.Label>
										<Form.Control
											type="file"
											onChange={handleImageMobileChange}
										/>
									</Form.Group>
									<Form.Group
										controlId="leagueRoundImageMobile"
										className="mb-3"
									>
										<Form.Label>Round Image Mobile</Form.Label>
										<Form.Control
											type="file"
											onChange={handleRoundImageMobileChange}
										/>
									</Form.Group>
									<div className="text-end">
										<Button variant="secondary" type="submit" size="sm">
											Upload Images
										</Button>
									</div>
								</Form>
							)}
						</Form>
						<Stack direction="horizontal" gap={2} className="justify-content-end">
							<Button variant="outline-secondary" onClick={resetForm}>
								Reset
							</Button>
							<Button variant="primary" onClick={handleSaveLeague}>
								{selectedLeague?._id ? "Update League" : "Create League"}
							</Button>
						</Stack>
					</Offcanvas.Body>
				</Offcanvas>
			</Container>
		</AdminLayout>
	);
};

export default AdminLeaguesPage;
